import { z } from 'zod';

import { StonePriceRuleBasis, StonePriceRuleStoneType } from '@nucleus-commerce/api-client/admin';

const DECIMAL = /^\d+(\.\d+)?$/;
const MONEY = z.object({ amount: z.number().int(), currency: z.string() });

/** The backend caps a margin at 100,000 percent. */
export const MAX_MARGIN_PCT = 100_000;

/**
 * One markup band. Carat bounds are typed as decimals; cost bounds are money in the base
 * currency (the shared supplier cost is in minor units). The range is [from, to).
 */
export const stonePriceRuleFormSchema = z
  .object({
    stoneType: z.enum(StonePriceRuleStoneType),
    basis: z.enum(StonePriceRuleBasis),
    /** Empty string: every store of the organization. */
    storeId: z.string(),
    fromCarat: z.string().trim(),
    toCarat: z.string().trim(),
    fromCost: MONEY.nullable(),
    toCost: MONEY.nullable(),
    marginPct: z.string().trim(),
  })
  .superRefine((v, ctx) => {
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: 'custom', path: [path], message });
    if (v.basis === 'carat') {
      if (v.fromCarat !== '' && !DECIMAL.test(v.fromCarat)) {
        issue('fromCarat', 'dictionary:stonePricing.validation.carat');
      }
      if (!DECIMAL.test(v.toCarat) || Number(v.toCarat) <= 0) {
        issue('toCarat', 'dictionary:stonePricing.validation.toRequired');
      } else if (v.fromCarat !== '' && Number(v.toCarat) <= Number(v.fromCarat)) {
        issue('toCarat', 'dictionary:stonePricing.validation.toAboveFrom');
      }
    } else {
      if (v.fromCost && v.fromCost.amount < 0) {
        issue('fromCost', 'dictionary:stonePricing.validation.cost');
      }
      if (!v.toCost || v.toCost.amount <= 0) {
        issue('toCost', 'dictionary:stonePricing.validation.toRequired');
      } else if (v.fromCost && v.toCost.amount <= v.fromCost.amount) {
        issue('toCost', 'dictionary:stonePricing.validation.toAboveFrom');
      }
    }
    if (!DECIMAL.test(v.marginPct) || Number(v.marginPct) <= 0) {
      issue('marginPct', 'dictionary:stonePricing.validation.margin');
    } else if (Number(v.marginPct) > MAX_MARGIN_PCT) {
      issue('marginPct', 'dictionary:stonePricing.validation.marginMax');
    }
  });

export type StonePriceRuleFormInput = z.input<typeof stonePriceRuleFormSchema>;
export type StonePriceRuleFormValues = z.output<typeof stonePriceRuleFormSchema>;
